import { html, LitElement } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASK_GENERAL_FORM } from '../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../ui/gzForm/formGroup/form-group';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../../../../../../ui/gzDropdown';
import { toggleDisabled, toggleVisibility } from '../../../../../../../../../utils/htmlElement.utils';
import {
  AntimalwareOnDemandScanTask,
  AntimalwareOnDemandScanTaskCpuMaxUsage,
  AntimalwareOnDemandScanTaskScanType,
  RecurrenceType,
  RepeatInterval,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzRadioGroupWebComponent } from '../../../../../../../../../ui/gzRadio';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../../../../../types/common/types';
import { convertScanTaskStartDateDbValueToUiValue } from '../utils/converters';
import { GzDatePickerWebComponent } from '../../../../../../../../../ui/gzDatePicker';
import { GzTimePickerWebComponent } from '../../../../../../../../../ui/gzTimePicker';
import {
  getRepeatIntervalFormOptions,
  getWeekDaysFormOptions,
  WeekDaysValues,
  WeekDayValueToScheduleFieldName,
} from '../../../../../../../../common/policyPanel/uiSettings/schedulerTemplate/types';
import { GzMultiSelectGroupWebComponent } from '../../../../../../../../../ui/gzMultiSelect';
import { MaxLengthValidator } from '../../../../../../../../../ui/gzForm/validators/max-length.validator';
import {
  getAntimalwareOnDemandScanTaskCpuMaxUsageFormOptions,
  MAX_TASK_NAME_LENGTH,
} from '../../../../../../utils/uiSettings/antimalware/antimalware.values';
import { GzSingleSelectWebComponent } from '../../../../../../../../../ui/gzSingleSelectDropdown';
import { ButtonColor } from '../../../../../../../../../ui/gzIconButton/types';
import {
  PolicyDependenciesContainer,
  PolicyTemplatesApiClients,
} from '../../../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { PolicyPanelManager } from '../../../../../../PolicyPanelManager';
import { GzDialogWebComponent } from '../../../../../../../../../ui/gzDialog';
import { GzButtonWebComponent } from '../../../../../../../../../ui/gzButton';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASK_GENERAL_FORM)
export class GzAntimalwareOnDemandScanTaskGeneralFormWebComponent extends LitElement {
  // general section
  @query('#name')
  private taskNameField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#tasks-scan-settings-miscellaneous-run-task-field')
  private scanSettingsMiscellaneousRunTaskField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#tasks-scan-settings-scanner-id-field')
  private scanSettingsScannerIdField: GzFormFieldWebComponent<GzSingleSelectWebComponent>;
  @query('#tasks-scan-settings-miscellaneous-shut-down-when-finished-field')
  private scanSettingsMiscellaneousShutDownWhenFinishedField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#tasks-pause-when-on-battery-field')
  private pauseWhenOnBatteryField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#tasks-cpu-usage-control-enable-field')
  private cpuUsageControlEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#tasks-cpu-usage-control-max-usage-field')
  private cpuUsageControlMaxUsageField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  // scheduler section
  @query('#tasks-scheduler-start-date-field')
  private schedulerStartDateField: GzFormFieldWebComponent<GzDatePickerWebComponent, any>;
  @query('#tasks-scheduler-start-time-field')
  private schedulerStartTimeField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
  @query('#tasks-scheduler-recurrence-type-field')
  private recurrenceTypeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#tasks-scheduler-recurrence-step-field')
  private recurrenceStepFormField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#tasks-scheduler-recurrence-step-type-field')
  private recurrenceStepTypeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#tasks-scheduler-recurrence-run-every-field')
  private recurrentDaysField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent>;
  @query('#tasks-recovery-mode-run-immediately-field-field')
  private recoveryModeRunImmediatelyField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-recovery-mode-run-scheduled-before-field')
  private recoveryModeRunScheduledBeforeField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#tasks-recovery-interval-field')
  private recoveryIntervalField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#tasks-recovery-run-per-field')
  private recoveryRunPerField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  public formGroup: FormGroup;
  private value: AntimalwareOnDemandScanTask;
  private scanType = AntimalwareOnDemandScanTaskScanType.QUICK;

  /**
   * Create form group and subscribe to valid.
   * @param changedProperties
   */
  protected firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.createFormGroup();
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.taskNameField.setError(error);
  }

  async createFormGroup() {
    this.taskNameField.Validators = [
      RequiredValidator(translateFn('Required field')),
      MaxLengthValidator(
        MAX_TASK_NAME_LENGTH,
        translateFn('The task name must not exceed {maxLength} characters.', {
          maxLength: MAX_TASK_NAME_LENGTH,
        })
      ),
    ];
    this.recurrenceStepFormField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.recurrenceStepTypeField.Validators = [RequiredValidator(translateFn('Required field'))];

    this.recurrentDaysField.touched = true;

    this.formGroup = FormGroup.build({
      taskName: this.taskNameField,
      recovery: {
        interval: this.recoveryIntervalField,
        runPer: this.recoveryRunPerField,
        mode: {
          runImmediately: this.recoveryModeRunImmediatelyField,
          runScheduledBefore: this.recoveryModeRunScheduledBeforeField,
        },
      },
      scheduler: {
        startDate: {
          date: this.schedulerStartDateField,
          time: this.schedulerStartTimeField,
        },
        recurrenceType: this.recurrenceTypeFormField,
        runEvery: this.recurrenceStepFormField,
        runPer: this.recurrenceStepTypeField,
        recurrentDays: this.recurrentDaysField,
      },
      scanSettings: {
        miscellaneous: {
          runTask: this.scanSettingsMiscellaneousRunTaskField,
          shutDownWhenFinished: this.scanSettingsMiscellaneousShutDownWhenFinishedField,
        },
        scannerId: this.scanSettingsScannerIdField,
      },
      pauseWhenOnBattery: this.pauseWhenOnBatteryField,
      cpuUsageControl: {
        enabled: this.cpuUsageControlEnableField,
        maximumUsage: this.cpuUsageControlMaxUsageField,
      },
    });
  }

  public async setFormGroupValue(scanType, value) {
    await this.firstUpdated;

    this.value = value;
    this.scanType = scanType;
    this.setDefaultViewForScanType();

    if (!value) {
      return;
    }

    const startDate = convertScanTaskStartDateDbValueToUiValue(value.scheduler.startDate);
    const weekDays = WeekDaysValues.filter(x => this.value?.scheduler[WeekDayValueToScheduleFieldName[x]]);

    this.formGroup.value = {
      taskName: this.value.taskName,
      recovery: this.value.recovery,
      scheduler: {
        startDate: startDate,
        recurrenceType: this.value.scheduler.recurrenceType,
        runEvery: this.value.scheduler.runEvery,
        runPer: this.value.scheduler.runPer,
        recurrentDays: weekDays,
      },
      scanSettings: {
        miscellaneous: {
          runTask: this.value.scanSettings.miscellaneous.runTask,
          shutDownWhenFinished: this.value.scanSettings.miscellaneous.shutDownWhenFinished,
        },
        scannerId: this.value.scanSettings.scannerId,
      },
      pauseWhenOnBattery: this.value.pauseWhenOnBattery,
      cpuUsageControl: this.value.cpuUsageControl,
    };
  }

  private setDefaultViewForScanType() {
    const isNetworkType = this.scanType === AntimalwareOnDemandScanTaskScanType.NETWORK;
    toggleVisibility(this.scanSettingsScannerIdField, isNetworkType);
    this.scanSettingsScannerIdField.disabled = !isNetworkType;

    if (isNetworkType) {
      this.setScannerDropdownOptions();
    } else {
      this.formGroup.removeFormChild(['scanSettings', 'customSettings', 'scannerId']);
    }
  }

  /**
   * Render specific content.
   */
  protected render() {
    const repeatIntervalOptionsForRecurrence = [RepeatInterval.HOURS, RepeatInterval.DAYS, RepeatInterval.WEEKDAYS];
    const repeatIntervalFormOptionsForRecurrence = repeatIntervalOptionsForRecurrence.map(
      (repeatInterval: RepeatInterval) => getRepeatIntervalFormOptions()[repeatInterval]
    );
    const repeatIntervalOptionsForStop = [RepeatInterval.HOURS, RepeatInterval.DAYS, RepeatInterval.WEEKDAYS];
    const repeatIntervalFormOptionsForStop = repeatIntervalOptionsForStop.map(
      (repeatInterval: RepeatInterval) => getRepeatIntervalFormOptions()[repeatInterval]
    );
    const weekDaysFormOptions = getWeekDaysFormOptions();

    return html`
      <!-- General - Details section -->
      <div id="tasks-general-details-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-2"
          >${translateFn('Details')}</gz-title
        >
        <gz-form-field id="name" class="label-mw-250 mt-1" label="${translateFn('Task name')}" required>
          <gz-input
            inputWidth="${InputWidthValues.LARGE}"
            gz-form-input
            placeholder="${translateFn('Enter the task name')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="tasks-scan-settings-miscellaneous-run-task-field">
          <gz-checkbox label="${translateFn('Run the task with low priority')}" gz-form-input></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="tasks-scan-settings-scanner-id-field" label="${translateFn('Use the following scanner')}">
          <gz-single-select
            gz-form-input
            labelWidth="${LabelWidthValues.LARGE}"
            placeholder="${translateFn('No Security Server selected')}"
            searchPlaceholderString="${translateFn('Search')}"
            noSearchResultsString="${translateFn('No Security Servers were found.')}"
          ></gz-single-select>
        </gz-form-field>

        <gz-form-field id="tasks-scan-settings-miscellaneous-shut-down-when-finished-field" no-impact>
          <gz-checkbox
            label="${translateFn('Shut down computer when scan is finished')}"
            gz-form-input
            @change="${this.onMiscellaneousShutDownWhenFinishedChange}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="tasks-pause-when-on-battery-field">
          <gz-checkbox
            label="${translateFn('Pause scan when computer is in Battery mode')}"
            gz-form-input
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="tasks-cpu-usage-control-enable-field">
          <gz-checkbox
            label="${translateFn('Enable CPU usage control')}"
            gz-form-input
            @change="${e => {
              toggleVisibility(this.cpuUsageControlMaxUsageField, e.detail.checked);
              if (!e.detail.checked) {
                this.cpuUsageControlMaxUsageField.value = AntimalwareOnDemandScanTaskCpuMaxUsage.HIGH;
              }
            }}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="tasks-cpu-usage-control-max-usage-field" class="helper-text" label="${translateFn('Set maximum CPU usage')}">
          <gz-dropdown
            gz-form-input
            .options=${getAntimalwareOnDemandScanTaskCpuMaxUsageFormOptions()}
            .value=${AntimalwareOnDemandScanTaskCpuMaxUsage.HIGH}
          ></gz-dropdown>
        </gz-form-field>
      </div>

      <!-- General - Scheduler section -->
      <div id="tasks-general-scheduler-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-4"
          >${translateFn('Scheduler')}</gz-title
        >

        <div id="tasks-scheduler-date-and-time-section" class="mt-1">
          <gz-form-field
            id="tasks-scheduler-start-date-field"
            class="label-mw-250 mr-2"
            label="${translateFn('Start date and time')}"
          >
            <gz-date-picker
              gz-form-input
              placeholder="day/month/year"
              labelWidth="${LabelWidthValues.MEDIUM}"
              navigateToSelected="True"
            ></gz-date-picker>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="tasks-scheduler-start-time-field" class="label-mw-250">
            <gz-time-picker gz-form-input></gz-time-picker>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>

        <div id="tasks-scheduler-recurrence-section">
          <gz-text class="hint-text mb-2" fontSize=${FontSizes.HEADING_6} fontWeight=${FontWeights.NORMAL}
            >${translateFn('Recurrence') + ':'}</gz-text
          >

          <gz-form-field class="helper-text" id="tasks-scheduler-recurrence-type-field">
            <gz-radio-group
              gz-form-input
              class="d-flex flex-column"
              .selectedOption=${RecurrenceType.SCHEDULE}
              @radioSelectionChanged=${this.onSchedulerRecurrenceTypeChanged}
            >
              <div class="radio" id="policy-panel-recurrence-schedule">
                <gz-radio
                  id="policy-panel-recurrence-schedule-radio"
                  label="${translateFn('Schedule task to run once every') + ':'}"
                  .value=${RecurrenceType.SCHEDULE}
                  wrapped
                ></gz-radio>

                <gz-form-field id="tasks-scheduler-recurrence-step-field" class="mr-2">
                  <gz-input-number gz-form-input .min=${1} .value=${1}></gz-input-number>
                  <gz-input-error></gz-input-error>
                </gz-form-field>
                <gz-form-field id="tasks-scheduler-recurrence-step-type-field">
                  <gz-dropdown
                    gz-form-input
                    .options=${repeatIntervalFormOptionsForRecurrence}
                    .value=${RepeatInterval.WEEKDAYS}
                  ></gz-dropdown>
                  <gz-input-error></gz-input-error>
                </gz-form-field>
              </div>

              <div class="radio" id="policy-panel-recurrence-weekdays">
                <gz-radio
                  id="policy-panel-recurrence-weekdays-radio"
                  label="${translateFn('Run task every') + ':'}"
                  .value=${RecurrenceType.WEEKDAYS}
                ></gz-radio>

                <gz-form-field id="tasks-scheduler-recurrence-run-every-field" class="disabled">
                  <gz-multiselect-group gz-form-input .options=${weekDaysFormOptions}></gz-multiselect-group>
                  <gz-input-error></gz-input-error>
                </gz-form-field>
              </div>
            </gz-radio-group>
          </gz-form-field>

          <gz-form-field id="tasks-recovery-mode-run-immediately-field-field">
            <gz-checkbox
              label="${translateFn('If scheduled run time is missed, run task as soon as possible')}"
              gz-form-input
              @change="${e => {
                toggleDisabled(this.recoveryModeRunScheduledBeforeField, !e.detail.checked);

                if (!e.detail.checked) {
                  this.recoveryModeRunScheduledBeforeField.value = false;
                }
              }}"
            ></gz-checkbox>
          </gz-form-field>

          <div class="checkbox-with-input">
            <gz-form-field class="checkbox-width-250 helper-text" id="tasks-recovery-mode-run-scheduled-before-field">
              <gz-checkbox
                label="${translateFn('Skip if next scheduled scan is due to start in less than') + ':'}"
                gz-form-input
                @change="${e => {
                  toggleDisabled(this.recoveryIntervalField, !e.detail.checked);
                  toggleDisabled(this.recoveryRunPerField, !e.detail.checked);
                }}"
              ></gz-checkbox>
            </gz-form-field>

            <gz-form-field id="tasks-recovery-interval-field" class="mr-2 disabled">
              <gz-input-number gz-form-input .min=${1} .max=${999} .value=${1}></gz-input-number>
            </gz-form-field>
            <gz-form-field id="tasks-recovery-run-per-field" class="disabled">
              <gz-dropdown
                gz-form-input
                .options=${repeatIntervalFormOptionsForStop}
                .value=${RepeatInterval.DAYS}
              ></gz-dropdown>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  private async setScannerDropdownOptions() {
    try {
      const clients = PolicyDependenciesContainer.getInstance().resolveDependency(
        'PolicyTemplates'
      ) as PolicyTemplatesApiClients;
      const policyId = PolicyPanelManager.getInstance().getPolicyId();
      const result = await clients.getEndpointsByActivePolicy(policyId);

      this.scanSettingsScannerIdField.Input.filterOptions = result.data.map(item => {
        return {
          label: item.name,
          value: item.specifics.endpointId,
        };
      });

      await this.scanSettingsScannerIdField.updateComplete;
      this.scanSettingsScannerIdField.value = this.value?.scanSettings?.scannerId || '';
    } catch (error) {
      this.setError(translateFn('Unable to load the Security Servers list.'));
    }
  }

  /**
   * Add double confirmation for shutting down endpoints.
   * @param event
   */
  private onMiscellaneousShutDownWhenFinishedChange(event: CustomEvent) {
    const value = event.detail.checked;

    // initial change is without impact
    // for the first change, values from backend are used
    if (this.scanSettingsMiscellaneousShutDownWhenFinishedField.hasAttribute('no-impact')) {
      this.scanSettingsMiscellaneousShutDownWhenFinishedField.removeAttribute('no-impact');
      return;
    }

    if (!value) {
      return;
    }

    // open dialog get response
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `
      <p>
        ${translateFn(
          'Target endpoints, including servers, will shut down immediately after the scan is finished. To avoid disrupting user activity or critical services, keep this option disabled.'
        )}
      </p>
      <gz-checkbox
        class="mt-2"
        id="confirmationCheckbox"
        label="${translateFn('I understand and agree')}"
      ></gz-checkbox>
    `;

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
      <gz-button id="buttonOne" type="full" color="primary">${translateFn('Save#preserve state')}</gz-button>
      <gz-button id="buttonTwo" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
    `;

    // cancel handler
    const onCancelHandler = () => {
      this.scanSettingsMiscellaneousShutDownWhenFinishedField.value = false;
      dialog.dismiss();
    };

    const dialogConfigs = {
      headline: translateFn('Shut down the computer after scan completes?'),
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
      onCloseHandler: onCancelHandler,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    // get buttons and checkbox & add listeners
    const footerBtnOne = dialogFooter.querySelector('#buttonOne') as GzButtonWebComponent;
    const footerBtnTwo = dialogFooter.querySelector('#buttonTwo') as GzButtonWebComponent;
    const confirmationCheckbox = dialogBody.querySelector('#confirmationCheckbox') as GzCheckboxWebComponent;

    if (footerBtnOne) {
      footerBtnOne.disabled = true;
      confirmationCheckbox.addEventListener('change', (e: CustomEvent) => (footerBtnOne.disabled = !e.detail.checked));
      footerBtnOne.addEventListener('click', () => dialog.close());
    }

    if (footerBtnTwo) {
      footerBtnTwo.addEventListener('click', onCancelHandler);
    }
  }

  /**
   * On radio selection change, disable unselected radio option.
   * @param event
   */
  private onSchedulerRecurrenceTypeChanged(event: CustomEvent) {
    const isScheduleType = event.detail.selectedValue === RecurrenceType.SCHEDULE;
    this.disableRecurrenceFields(!isScheduleType, isScheduleType);
  }

  /**
   * Enable/disable form fields based on received parameters.
   * @param isScheduleDisabled
   * @param isWeekdaysDisabled
   */
  private disableRecurrenceFields(isScheduleDisabled: boolean, isWeekdaysDisabled: boolean) {
    // disable/enable recurrence fields - run once every
    toggleDisabled(this.recurrenceStepFormField, isScheduleDisabled);
    toggleDisabled(this.recurrenceStepTypeField, isScheduleDisabled);

    // disable/enable recurrence fields & add validation - weekdays
    toggleDisabled(this.recurrentDaysField, isWeekdaysDisabled);
    this.recurrentDaysField.Validators = isWeekdaysDisabled
      ? []
      : [RequiredValidator(translateFn('Select at least one day of the week.'))];
  }
}
